import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Save, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/apiConfig";
import ContentPublicPreview, { announceContentSaved } from "@/components/ContentPublicPreview";

function slugify(str) {
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export default function GestionArticles() {
  const { user } = useAuth();
  const emptyForm = () => ({ title: "", slug: "", content: "", excerpt: "", image: "", author: user?.prenom || "Eudemonie", status: "draft" });

  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [initialForm, setInitialForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);

  const load = () => axios.get(`${API}/admin/articles`).then(r => setArticles(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Erreur de chargement"));

  useEffect(() => { load(); }, []);

  const openNew = () => { const next=emptyForm();setForm(next);setInitialForm(next);setEditing("new"); };
  const openEdit = a => { const next={ title: a.title, slug: a.slug, content: a.content, excerpt: a.excerpt || "", image: a.image || "", author: a.author || user?.prenom || "Eudemonie", status: a.status };setForm(next);setInitialForm(next);setEditing(a.id); };
  const close = () => { const next=emptyForm();setEditing(null);setForm(next);setInitialForm(next); };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value, ...(name === "title" && editing === "new" ? { slug: slugify(value) } : {}) }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing === "new") await axios.post(`${API}/admin/articles`, form);
      else await axios.put(`${API}/admin/articles/${editing}`, form);
      announceContentSaved();
      toast.success(editing === "new" ? "Article créé !" : "Article mis à jour !");
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await axios.delete(`${API}/admin/articles/${id}`);
    toast.success("Article supprimé");
    load();
  };

  if (editing !== null) {
    return (
      <ContentPublicPreview url={form.status==="published"&&form.slug?`/blog/${form.slug}`:"/blog"} title={form.slug?`Article · ${form.title||form.slug}`:"Article de blog"} status={editing!=="new"&&form.status==="published"?"published":"draft"} dirty={JSON.stringify(form)!==JSON.stringify(initialForm)}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-playfair text-3xl text-charcoal">{editing === "new" ? "Nouvel article" : "Modifier l'article"}</h1>
          <button onClick={close} className="p-2 text-charcoal-muted hover:text-charcoal"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-2xl p-6 border border-sage/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Titre *</label>
              <input name="title" value={form.title} onChange={handleChange} required placeholder="Titre de l'article"
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all" />
            </div>
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Slug (URL)</label>
              <input name="slug" value={form.slug} onChange={handleChange} placeholder="url-de-l-article"
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all" />
            </div>
          </div>
          <div>
            <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Extrait</label>
            <textarea name="excerpt" value={form.excerpt} onChange={handleChange} rows={2} placeholder="Courte description affichée dans les listes..."
              className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all resize-none" />
          </div>
          <div>
            <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Contenu (HTML)</label>
            <textarea name="content" value={form.content} onChange={handleChange} rows={12} placeholder="<h2>...</h2><p>...</p>"
              className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all resize-y font-mono" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Image (URL)</label>
              <input name="image" value={form.image} onChange={handleChange} placeholder="https://..."
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all" />
            </div>
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Auteur</label>
              <input name="author" value={form.author} readOnly
                className="w-full border border-sage/10 rounded-xl px-4 py-2.5 font-manrope text-sm bg-bone/50 text-charcoal-muted cursor-not-allowed" />
            </div>
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Statut</label>
              <select name="status" value={form.status} onChange={handleChange}
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none transition-all bg-white">
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} data-testid="article-save-btn"
              className="btn-primary flex items-center gap-2 disabled:opacity-60">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save size={15} /> Enregistrer</>}
            </button>
            <button type="button" onClick={close} className="btn-secondary">Annuler</button>
          </div>
        </form>
      </div>
      </ContentPublicPreview>
    );
  }

  return (
    <ContentPublicPreview url="/blog" title="Articles et conseils">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-playfair text-3xl text-charcoal">Articles</h1>
          <p className="font-manrope text-sm text-charcoal-muted">{articles.length} article{articles.length > 1 ? "s" : ""}</p>
        </div>
        <button onClick={openNew} data-testid="new-article-btn" className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Nouvel article
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-sage/10 overflow-hidden">
        {articles.length === 0 ? (
          <div className="py-16 text-center font-manrope text-charcoal-muted">Aucun article. Créez le premier !</div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-sage/10 bg-bone/30">
              <tr>
                {["Titre", "Statut", "Auteur", "Actions"].map(h => (
                  <th key={h} className="text-left font-manrope text-xs text-charcoal-muted px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {articles.map((a, i) => (
                <tr key={a.id || i} data-testid={`article-row-${i}`} className="border-b border-sage/5 last:border-0 hover:bg-bone/20 transition-colors">
                  <td className="px-5 py-3">
                    <p className="font-manrope text-sm font-medium text-charcoal">{a.title}</p>
                    <p className="font-manrope text-xs text-charcoal-muted">/blog/{a.slug}</p>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`font-manrope text-xs px-2.5 py-1 rounded-full ${a.status === "published" ? "bg-sage/10 text-sage-dark" : "bg-bone text-charcoal-muted"}`}>
                      {a.status === "published" ? "Publié" : "Brouillon"}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-manrope text-sm text-charcoal-muted">{a.author}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(a)} data-testid={`edit-article-${i}`} className="p-1.5 text-charcoal-muted hover:text-sage transition-colors"><Edit size={15} /></button>
                      <button onClick={() => handleDelete(a.id)} data-testid={`delete-article-${i}`} className="p-1.5 text-charcoal-muted hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </ContentPublicPreview>
  );
}
